import React from 'react';
import { useTranslation } from 'react-i18next';
import { Agent } from '@/types/Agents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, PhoneCall, Timer, TrendingUp, Users } from 'lucide-react';
import { SubscriptionUsageCard } from '@/components/page-components/billing/SubscriptionUsageCard';
import { getUsageMetrics } from '@/utils/subscriptionUtils';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import { useDateFormat } from '@/hooks/useDateFormat';

interface AgentDetailsProps {
  agent: Agent;
  className?: string;
}

const AgentDashboard: React.FC<AgentDetailsProps> = ({ agent, className }) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const subscription = agent.subscription;
  const { hasUsage: hasSubscriptionUsage } = getUsageMetrics(subscription);

  // Calculate average duration
  const totalDuration = (agent.conversations || []).reduce(
    (sum, conv) => sum + (conv.duration || 0),
    0
  );
  const avgDuration = agent.conversations?.length
    ? totalDuration / agent.conversations.length
    : 0;

  // Calculate total contacts based on unique caller_id and agent_id combinations
  const uniqueContacts = new Set<string>();
  (agent.conversations || []).forEach(conv => {
    const contactKey = `${conv.agent_id}-${conv.caller_id}`;
    uniqueContacts.add(contactKey);
  });
  const totalContacts = uniqueContacts.size;

  // Calculate recent activity (conversations in last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentActivity = (agent.conversations || []).filter(
    conv => new Date(conv.created_at) > sevenDaysAgo
  ).length;

  // Calculate peak hours (most active hour of the day)
  const hourCounts = new Array(24).fill(0);
  (agent.conversations || []).forEach(conv => {
    const hour = new Date(conv.created_at).getHours();
    hourCounts[hour]++;
  });
  const peakHour = hourCounts.indexOf(Math.max(...hourCounts));

  // Prepare hourly activity data for chart
  const hourlyActivityData = hourCounts.map((count, hour) => ({
    hour: `${hour}:00`,
    conversations: count,
  }));

  // Calculate conversations per day for the last 7 days
  const dailyCounts = new Array(7).fill(0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  (agent.conversations || []).forEach(conv => {
    const convDate = new Date(conv.created_at);
    convDate.setHours(0, 0, 0, 0);
    const daysAgo = Math.floor(
      (today.getTime() - convDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysAgo >= 0 && daysAgo < 7) {
      dailyCounts[6 - daysAgo]++; // Reverse order: oldest to newest
    }
  });

  // Prepare daily activity data for chart
  const dailyActivityData = dailyCounts.map((count, index) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (6 - index));
    const dayName = df.weekdayShort(date);
    return {
      day: dayName,
      conversations: count,
    };
  });

  // Calculate average duration per day for the last 7 days
  const dailyDurations: Array<{ total: number; count: number }> = new Array(7)
    .fill(null)
    .map(() => ({ total: 0, count: 0 }));

  (agent.conversations || []).forEach(conv => {
    const convDate = new Date(conv.created_at);
    convDate.setHours(0, 0, 0, 0);
    const daysAgo = Math.floor(
      (today.getTime() - convDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysAgo >= 0 && daysAgo < 7 && conv.duration) {
      const index = 6 - daysAgo;
      dailyDurations[index].total += conv.duration;
      dailyDurations[index].count += 1;
    }
  });

  // Prepare daily duration trend data
  const dailyDurationData = dailyDurations.map((data, index) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (6 - index));
    const dayName = df.weekdayShort(date);
    const avgDuration = data.count > 0 ? data.total / data.count : 0;
    return {
      day: dayName,
      avgDuration: Math.round(avgDuration * 10) / 10, // Round to 1 decimal
    };
  });

  // Calculate duration distribution
  const durationBuckets = [
    { label: '0-30s', min: 0, max: 30, count: 0 },
    { label: '30s-1m', min: 30, max: 60, count: 0 },
    { label: '1-2m', min: 60, max: 120, count: 0 },
    { label: '2-5m', min: 120, max: 300, count: 0 },
    { label: '5m+', min: 300, max: Infinity, count: 0 },
  ];

  (agent.conversations || []).forEach(conv => {
    const duration = conv.duration || 0;
    for (const bucket of durationBuckets) {
      if (duration >= bucket.min && duration < bucket.max) {
        bucket.count++;
        break;
      }
    }
  });

  // Prepare duration distribution data (only include buckets with data)
  const durationDistributionData = durationBuckets
    .filter(bucket => bucket.count > 0)
    .map(bucket => ({
      range: bucket.label,
      count: bucket.count,
    }));

  const hasConversations =
    agent.conversations && agent.conversations.length > 0;

  const renderSubscriptionUsage = () => {
    if (!hasSubscriptionUsage) return null;
    return <SubscriptionUsageCard subscription={subscription} showPlanName />;
  };

  if (!hasConversations && !hasSubscriptionUsage) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 text-4xl text-muted-foreground">
          {t('agent.dashboard.no_data.translation', 'No data available')}
        </div>
        <div className="text-xl text-muted-foreground">
          {t(
            'agent.dashboard.no_data_description.translation',
            'Dashboard data will appear here once the agent starts receiving conversations'
          )}
        </div>
      </div>
    );
  }

  if (!hasConversations) {
    return (
      <div className={`flex flex-col gap-4 ${className || ''}`}>
        {renderSubscriptionUsage()}
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 text-4xl text-muted-foreground">
            {t('agent.dashboard.no_data.translation', 'No data available')}
          </div>
          <div className="text-xl text-muted-foreground">
            {t(
              'agent.dashboard.no_activity_description.translation',
              'Activity charts will appear here once the agent starts receiving conversations'
            )}
          </div>
        </div>
      </div>
    );
  }

  // Chart configuration
  const hourlyChartConfig = {
    conversations: {
      label: 'Conversations',
      color: 'hsl(var(--brand))',
    },
  };

  const dailyChartConfig = {
    conversations: {
      label: 'Conversations',
      color: 'hsl(var(--brand))',
    },
  };

  const durationTrendConfig = {
    avgDuration: {
      label: 'Avg Duration (s)',
      color: 'hsl(var(--brand))',
    },
  };

  const durationDistributionConfig = {
    count: {
      label: 'Conversations',
      color: 'hsl(var(--brand))',
    },
  };

  return (
    <div className={`flex flex-col gap-4 ${className || ''}`}>
      {renderSubscriptionUsage()}

      {/* Statistics Card */}
      <Card className="bg-card">
        <CardHeader className="pb-2">
          <CardTitle>
            {t('agent.dashboard.overview.title.translation', {
              name: agent.name,
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="flex items-center justify-between border-b border-border py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4 text-muted-foreground" />
              {t('agent.dashboard.overview.total_contacts.translation')}
            </div>
            <p className="font-semibold">{totalContacts}</p>
          </div>
          <div className="flex items-center justify-between border-b border-border py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <PhoneCall className="h-4 w-4 text-muted-foreground" />
              {t('agent.dashboard.overview.total_conversations.translation')}
            </div>
            <p className="font-semibold">{agent.conversations?.length || 0}</p>
          </div>
          <div className="flex items-center justify-between border-b border-border py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Timer className="h-4 w-4 text-muted-foreground" />
              {t('agent.dashboard.overview.avg_duration.translation')}
            </div>
            <p className="font-semibold">
              {t('agent.dashboard.overview.avg_duration_value.translation', {
                value: avgDuration.toFixed(1),
              })}
            </p>
          </div>
          <div className="flex items-center justify-between border-b border-border py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              {t('agent.dashboard.overview.calls_last_7_days.translation')}
            </div>
            <p className="font-semibold">{recentActivity}</p>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4 text-muted-foreground" />
              {t('agent.dashboard.overview.peak_hour.translation')}
            </div>
            <p className="font-semibold">{peakHour}:00</p>
          </div>
        </CardContent>
      </Card>

      {/* Daily Activity Chart */}
      {dailyActivityData.some(d => d.conversations > 0) && (
        <Card className="bg-card">
          <CardHeader className="pb-2">
            <CardTitle>
              {t(
                'agent.dashboard.daily_activity.translation',
                'Conversations (Last 7 Days)'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={dailyChartConfig}
              className="h-[200px] w-full"
            >
              <LineChart data={dailyActivityData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis
                  dataKey="day"
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  cursor={{ stroke: 'hsl(var(--brand))', strokeWidth: 1 }}
                />
                <Line
                  type="monotone"
                  dataKey="conversations"
                  stroke="hsl(var(--brand))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--brand))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Hourly Activity Chart */}
      {hourlyActivityData.some(h => h.conversations > 0) && (
        <Card className="bg-card">
          <CardHeader className="pb-2">
            <CardTitle>
              {t(
                'agent.dashboard.hourly_activity.translation',
                'Activity by Hour'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={hourlyChartConfig}
              className="h-[200px] w-full"
            >
              <BarChart data={hourlyActivityData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis
                  dataKey="hour"
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  cursor={{ fill: 'var(--color-conversations)', opacity: 0.1 }}
                />
                <Bar
                  dataKey="conversations"
                  fill="var(--color-conversations)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Average Duration Trend Chart */}
      {dailyDurationData.some(d => d.avgDuration > 0) && (
        <Card className="bg-card">
          <CardHeader className="pb-2">
            <CardTitle>
              {t(
                'agent.dashboard.duration_trend.translation',
                'Average Duration Trend (Last 7 Days)'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={durationTrendConfig}
              className="h-[200px] w-full"
            >
              <LineChart data={dailyDurationData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis
                  dataKey="day"
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={true}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent labelFormatter={label => `${label}`} />
                  }
                  cursor={{ stroke: 'hsl(var(--brand))', strokeWidth: 1 }}
                />
                <Line
                  type="monotone"
                  dataKey="avgDuration"
                  stroke="hsl(var(--brand))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--brand))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Duration Distribution Chart */}
      {durationDistributionData.length > 0 && (
        <Card className="bg-card">
          <CardHeader className="pb-2">
            <CardTitle>
              {t(
                'agent.dashboard.duration_distribution.translation',
                'Conversation Duration Distribution'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={durationDistributionConfig}
              className="h-[200px] w-full"
            >
              <BarChart data={durationDistributionData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis
                  dataKey="range"
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis
                  className="text-xs"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  cursor={{ fill: 'var(--color-count)', opacity: 0.1 }}
                />
                <Bar
                  dataKey="count"
                  fill="var(--color-count)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgentDashboard;
